import { useUsageActions, useBrandBreakdown } from "@/hooks/use-usage";

function formatMonthDay(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}

export function ViewAllUsageModal({ onClose }: { onClose: () => void }) {
  const actions = useUsageActions(200);
  const breakdown = useBrandBreakdown();

  const breakdownRows = (breakdown.data?.rows ?? [])
    .filter((r: any) => Number(r.totalCredits) > 0)
    .sort((a: any, b: any) => Number(b.totalCredits) - Number(a.totalCredits));

  const actionRows = actions.data?.rows ?? [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      data-testid="view-all-usage-modal"
    >
      <div
        className="max-h-[85vh] w-full max-w-3xl overflow-auto rounded bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Usage history</h2>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-800" data-testid="view-all-close">✕</button>
        </div>

        {breakdownRows.length > 0 && (
          <section className="mb-6">
            <h3 className="mb-2 text-sm font-medium">Usage by brand (last 30 days)</h3>
            <div className="rounded border border-neutral-200">
              {breakdownRows.map((r: any) => (
                <div
                  key={String(r.brandId ?? "unattributed")}
                  className="flex items-center justify-between border-b border-neutral-100 px-3 py-2 text-sm last:border-b-0"
                >
                  <span>{r.brandName ?? "Unattributed"}</span>
                  <span className="font-mono tabular-nums">
                    {Math.round(Number(r.totalCredits)).toLocaleString()} credits
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        <section>
          <h3 className="mb-2 text-sm font-medium">All actions</h3>
          {actions.isLoading && <div className="text-sm text-neutral-500">Loading…</div>}
          {actions.error && <div className="text-sm text-red-600">Failed to load actions.</div>}
          {!actions.isLoading && actionRows.length === 0 && (
            <div className="rounded border border-neutral-200 p-6 text-center text-sm text-neutral-500">
              No actions yet.
            </div>
          )}
          {actionRows.length > 0 && (
            <div className="divide-y divide-neutral-100 rounded border border-neutral-200">
              {actionRows.map((a: any) => (
                <div key={a.id} className="flex items-center justify-between px-3 py-2 text-sm">
                  <div>
                    <div className="font-medium text-neutral-800">{a.actionLabel}</div>
                    <div className="text-xs text-neutral-500">
                      {a.brandName ? `${a.brandName} · ` : ""}
                      {formatMonthDay(a.startedAt)}
                      {a.status && a.status !== "completed" ? ` · ${a.status}` : ""}
                    </div>
                    {Array.isArray(a.breakdown) && a.breakdown.length > 0 && (
                      <div className="mt-0.5 text-[11px] text-neutral-400">
                        {a.breakdown
                          .map((b: any) => `${b.label} ${Number(b.credits).toFixed(0)}`)
                          .join(" · ")}
                      </div>
                    )}
                  </div>
                  <div className="font-mono tabular-nums text-neutral-700">
                    −{Number(a.creditsActual).toFixed(0)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
